import React from 'react';
import { useClientTypeQuery } from 'api';
import {
  AdminSelect,
  Col,
  CountrySelect,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Tag,
  Typography,
} from 'components';
import { FilterDrawer } from 'components/common/filterDrawer';
import { CalendarIcon } from 'components/common/icons';
import { IFilterParams } from 'pages/admin/clients/useClients';
import { CLIENT_PRIORITY_LEVEL } from 'staticData/form';
import { useFilterDrawer, VERIFIED_STATUS } from './useFilterDrawer';
// import { Select as AntdSelect } from 'antd';

const { RangePicker } = DatePicker;

interface IClientFilterDrawer {
  closeDrawer: () => void;
  filterDrawerState: boolean;
  applyFilters: (values: IFilterParams) => void;
  resetFilters: () => void;
  form: FormInstance;
}

export const ClientFilterDrawer = (props: IClientFilterDrawer) => {
  const { closeDrawer, filterDrawerState, applyFilters, resetFilters, form } = props;
  const { data: clientTypeData = [] } = useClientTypeQuery();
  const { handleTagSelect, handleTagRemove, availableTags, clearFilters, clearFieldValidation } = useFilterDrawer({
    form,
    resetFilters,
  });
  return (
    <FilterDrawer
      title="Filter"
      placement="left"
      width={471}
      styles={{
        body: {
          marginBottom: 80,
        },
      }} // Add space for drawer actions
      onClose={closeDrawer}
      open={filterDrawerState}
      onApply={() => form.submit()}
      onClear={clearFilters}
      onExpand={closeDrawer}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={applyFilters}
        initialValues={{
          dealCountMax: null,
          dealCountMin: null,
          dealAmountMin: null,
          dealAmountMax: null,
          createdDateFrom: null,
          createdDateTo: null,
          lastUpdatedDateFrom: null,
          lastUpdatedDateTo: null,
          salesManagerId: null,
          purchasingManagerId: null,
          countryId: null,
          clientTypeId: null,
          priorityLevel: null,
          verifiedStatus: null,
        }}
      >
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={16} wrap={false}>
              <Col span={12}>
                <Form.Item
                  name="dealCountMin"
                  label={'Deal Count'}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const dealCountMax = getFieldValue('dealCountMax');
                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                        const numDealCountMax = dealCountMax ? Number(dealCountMax) : NaN; // Convert dealCountMin to a number

                        if (numValue && numValue > numDealCountMax) {
                          return Promise.reject(
                            new Error('Min Deal count must be smaller than or equal to Max Deal count.')
                          );
                        }
                        clearFieldValidation(form, 'dealCountMax');
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  validateTrigger="onChange"
                >
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ display: 'none' }}></span>}
                  name="dealCountMax"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const dealCountMin = getFieldValue('dealCountMin');
                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                        const numDealCountMin = dealCountMin ? Number(dealCountMin) : NaN; // Convert dealCountMin to a number

                        if (numValue && numValue < numDealCountMin) {
                          return Promise.reject(
                            new Error('Max Deal count must be greater than or equal to Min Deal count.')
                          );
                        }
                        clearFieldValidation(form, 'dealCountMin');
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  validateTrigger="onChange"
                >
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false}>
              <Col span={12}>
                <Form.Item
                  name="dealAmountMin"
                  label={'Deal Amount'}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const dealAmountMax = getFieldValue('dealAmountMax');
                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                        const numDealAmountMax = dealAmountMax ? Number(dealAmountMax) : NaN; // Convert dealCountMin to a number

                        if (numValue && numValue > numDealAmountMax) {
                          return Promise.reject(
                            new Error('Min Deal amount must be smaller than or equal to Max Deal amount.')
                          );
                        }
                        clearFieldValidation(form, 'dealAmountMax');
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ display: 'none' }}></span>}
                  name="dealAmountMax"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const dealAmountMin = getFieldValue('dealAmountMin');
                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                        const numDealAmountMin = dealAmountMin ? Number(dealAmountMin) : NaN; // Convert dealCountMin to a number

                        if (numValue && numValue < numDealAmountMin) {
                          return Promise.reject(
                            new Error('Max Deal amount must be greater than or equal to Min Deal amount.')
                          );
                        }
                        clearFieldValidation(form, 'dealAmountMin');
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Form.Item name="createdDate" label={'Created Date'}>
              <RangePicker suffixIcon={<CalendarIcon />} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="lastUpdatedDate" label={'Last Updated Date'}>
              <RangePicker suffixIcon={<CalendarIcon />} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="lastActivityDate" label={'Last Activity Date'}>
              <RangePicker suffixIcon={<CalendarIcon />} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="salesManagerId" label={'Sales Manager'}>
              <AdminSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="purchasingManagerId" label={'Purchasing Manager'}>
              <AdminSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="countryId" label={'Country'}>
              <CountrySelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="clientTypeId" label={'Client Type'}>
              <Select options={clientTypeData} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="priorityLevel" label={'Priority Level'}>
              <Select options={CLIENT_PRIORITY_LEVEL} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="verifiedStatus" label={'Verified Status'}>
              <Select options={VERIFIED_STATUS} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={'Tags'}>
              <Form.List name="tags">
                {(fields, { add, remove }) => (
                  <Row gutter={[0, 12]}>
                    <Col>
                      <Row gutter={[6, 6]}>
                        {fields.map(({ key, name }) => (
                          <Col key={key} style={{ height: 26 }}>
                            <Tag
                              bordered={false}
                              className={'custom-tag'}
                              closable
                              title={form.getFieldValue(['tags', name])}
                              onClose={() => {
                                const removedTag = form.getFieldValue(['tags', name]);
                                handleTagRemove(removedTag);
                                remove(name);
                              }}
                            >
                              {form.getFieldValue(['tags', name])}
                            </Tag>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Divider />
                    <Col>
                      <Row gutter={[6, 6]}>
                        {availableTags?.length ? (
                          availableTags.map((tag: string) => (
                            <Col key={tag} style={{ height: 26 }}>
                              <Tag
                                bordered={false}
                                key={tag}
                                className={'custom-tag'}
                                onClick={() => {
                                  add(tag);
                                  handleTagSelect(tag);
                                }}
                                style={{
                                  cursor: 'pointer',
                                }}
                              >
                                {tag}
                              </Tag>
                            </Col>
                          ))
                        ) : !fields?.length ? (
                          <Typography> Not Set </Typography>
                        ) : null}
                      </Row>
                    </Col>
                  </Row>
                )}
              </Form.List>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
};
